import {
  parseLoadSubsetOptions,
  type QueryCollectionMeta,
  type SimpleComparison,
} from "@tanstack/query-db-collection";

export function sanitizeCollectionScopeToken(value: string): string {
  return value.replace(/[^a-zA-Z0-9:_-]/g, "_");
}

export function toScopedCollectionId(
  baseId: string,
  sessionScopeToken: string,
): string {
  return `${baseId}:${sanitizeCollectionScopeToken(sessionScopeToken)}`;
}

export function fieldMatches(
  filter: SimpleComparison,
  fieldName: string,
): boolean {
  return filter.field.at(-1) === fieldName;
}

export function parseSubsetFilters(
  meta: QueryCollectionMeta | undefined,
): {
  filters: SimpleComparison[];
  limit?: number;
} {
  const parsed = parseLoadSubsetOptions(meta?.loadSubsetOptions);
  return {
    filters: parsed.filters,
    limit: parsed.limit,
  };
}

export function extractStringFilterValues(
  filters: SimpleComparison[],
  fieldNames: readonly string[],
): string[] {
  return filters.flatMap((filter) => {
    if (!fieldNames.some((fieldName) => fieldMatches(filter, fieldName))) {
      return [];
    }

    if (filter.operator === "eq" && typeof filter.value === "string") {
      return [filter.value];
    }

    if (filter.operator === "in" && Array.isArray(filter.value)) {
      return filter.value.filter(
        (value): value is string => typeof value === "string",
      );
    }

    return [];
  });
}

export function extractEqFilterValue<T>(
  filters: SimpleComparison[],
  fieldName: string,
): T | undefined {
  const match = filters.find(
    (filter) => fieldMatches(filter, fieldName) && filter.operator === "eq",
  );
  return match?.value as T | undefined;
}

export function extractInFilterValues<T>(
  filters: SimpleComparison[],
  fieldName: string,
): T[] {
  const match = filters.find(
    (filter) => fieldMatches(filter, fieldName) && filter.operator === "in",
  );

  if (!match || !Array.isArray(match.value)) {
    return [];
  }

  return match.value as T[];
}
